import { DataHandler } from '@scarybeasties/phaser-framework-core/modules';
import { App } from '../App';
import { Level, LevelName, LevelNameValues, Worlds } from './levels/level';
import {Sounds} from "~/js/sounds";
import CharacterValues = Sounds.CharacterValues;

export interface GameData {
  [GameDataHandler.keys.HighScore]?: number;
  [GameDataHandler.keys.LevelHighScores]?: Partial<Record<LevelNameValues, number>>;
  [GameDataHandler.keys.LevelStarScores]?: Partial<Record<LevelNameValues, number>>;
  [GameDataHandler.keys.EarnedPoints]?: number;
  [GameDataHandler.keys.UnlockedLevels]?: LevelNameValues[];
  [GameDataHandler.keys.JustUnlockedLevels]?: LevelNameValues[];
}

/**
 * Used to store game data that needs to be held permanently, or instance data
 * that needs to be available for entire game life cycle.
 *
 * For data only applicable to a single run/play use RunSettingsHandler instead.
 *
 * ### Adding New Data
 * To add a new bit of data, add it to the keys section, add a default to one
 * of the defaults (permanentDefaultData or instanceDefaultData), and then add
 * static convenience methods to access or alter the data in the Phaser Registry.
 *
 * If you want the data to be saved permanently, you also need to add a new
 * event, add a change method, and bind the change method to the Phaser Registry.
 *
 */
export class GameDataHandler extends DataHandler {
  // Replaces strings to access Phaser registry values.
  public static keys = {
    // Instance Keys.
    Score: 'score',
    BlocksBroken: 'BlocksBroken',

    // Permanent Keys
    HighScore: 'highScore',
    LevelHighScores: 'levelHighScores',
    LevelStarScores: 'levelStarScores',
    EarnedPoints: 'earnedPoints',
    UnlockedLevels: 'unlockedLevels',
    JustUnlockedLevels: 'justUnlockedLevels',
    LastLevelPlayed: 'lastLevelPlayed',
    LastWorldPlayed: 'lastWorldPlayed',
    SelectedCharacter: 'selectedCharacter'
  } as const;

  // Replaces strings to access Phaser registry events.
  public static events = {
    ChangeScore: `changedata-${GameDataHandler.keys.Score}`,
    ChangeHighScore: `changedata-${GameDataHandler.keys.HighScore}`,
    ChangeLevelHighScores: `changedata-${GameDataHandler.keys.LevelHighScores}`,
    ChangeLevelStarScores: `changedata-${GameDataHandler.keys.LevelStarScores}`,
    ChangeEarnedPoints: `changedata-${GameDataHandler.keys.EarnedPoints}`,
    ChangeUnlockedLevels: `changedata-${GameDataHandler.keys.UnlockedLevels}`,
    ChangeJustUnlockedLevels: `changedata-${GameDataHandler.keys.JustUnlockedLevels}`
  } as const;

  protected app: App;
  protected permanentDefaultData: GameData;
  protected instanceDefaultData: { [key: string]: any };
  protected gameData: GameData;

  constructor(app: App) {
    super();

    this.app = app;

    // Set any defaults for data we're storing in gameData here.
    this.permanentDefaultData = {
      [GameDataHandler.keys.HighScore]: 0,
      [GameDataHandler.keys.LevelHighScores]: {},
      [GameDataHandler.keys.LevelStarScores]: {},
      [GameDataHandler.keys.EarnedPoints]: 0,
      [GameDataHandler.keys.UnlockedLevels]: [LevelName.Level1],
      [GameDataHandler.keys.JustUnlockedLevels]: []
    };

    // Set any instance defaults for data we're not storing here.
    this.instanceDefaultData = {
      score: 0
    };

    this.gameData = Object.assign({}, this.permanentDefaultData);
  }

  //#region Static Phaser Registry Convenience Methods

  public static showOnboarding(scene: Phaser.Scene) {
    let levelHighScores: Partial<Record<LevelNameValues, number>> = scene.game.registry.get(GameDataHandler.keys.LevelHighScores);
    return Object.keys(levelHighScores).length <= 0;
  }

  public static getHighScore(scene: Phaser.Scene, level: LevelNameValues) {
    let levelHighScores: Partial<Record<LevelNameValues, number>> = scene.game.registry.get(GameDataHandler.keys.LevelHighScores);
    let levelHighScore = levelHighScores[level];

    // Might be first play so we won't have a highScore.
    if (!levelHighScore) levelHighScore = 0;

    return levelHighScore;
  }

  public static setHighScore(scene: Phaser.Scene, level: LevelNameValues, newScore: number) {
    let levelHighScores: Partial<Record<LevelNameValues, number>> = scene.game.registry.get(GameDataHandler.keys.LevelHighScores);
    let levelHighScore = levelHighScores[level];

    // Might be first play so we won't have a highScore.
    if (!levelHighScore) levelHighScore = 0;

    if (newScore > levelHighScore) {
      levelHighScores[level] = newScore;
      scene.game.registry.set(GameDataHandler.keys.LevelHighScores, levelHighScores);
    }
  }

  public static getStarScore(scene: Phaser.Scene, level: LevelNameValues) {
    let levelStarScores: Partial<Record<LevelNameValues, number>> = scene.game.registry.get(GameDataHandler.keys.LevelStarScores);
    let levelStarScore = levelStarScores[level];

    // Might be first play so we won't have a highScore.
    if (!levelStarScore) levelStarScore = 0;

    return levelStarScore;
  }

  public static setStarScore(scene: Phaser.Scene, level: LevelNameValues, newScore: number) {
    const maxStars = 3;

    let levelStarScores: Partial<Record<LevelNameValues, number>> = scene.game.registry.get(GameDataHandler.keys.LevelStarScores);
    let levelStarScore = levelStarScores[level];

    // Might be first play so we won't have a highScore.
    if (!levelStarScore) levelStarScore = 0;

    if (newScore > levelStarScore && newScore <= maxStars) {
      levelStarScores[level] = newScore;
      scene.game.registry.set(GameDataHandler.keys.LevelStarScores, levelStarScores);
    }
  }

  public static canPurchase(scene: Phaser.Scene, purchaseAmount: number): boolean {
    let earnedPoints = scene.game.registry.get(GameDataHandler.keys.EarnedPoints);
    return earnedPoints >= purchaseAmount;
  }

  public static spendEarnedPoints(scene: Phaser.Scene, amountToSpend: number) {
    scene.game.registry.inc(GameDataHandler.keys.EarnedPoints, -amountToSpend);
  }

  public static getLastLevelPlayed(scene: Phaser.Scene): LevelNameValues | undefined {
    return scene.game.registry.get(GameDataHandler.keys.LastLevelPlayed);
  }

  public static getLastWorldPlayed(scene: Phaser.Scene): number
  {
    return scene.game.registry.get(GameDataHandler.keys.LastWorldPlayed) ?? GameDataHandler.getWorldForLevel(scene,GameDataHandler.getLastLevelUnlocked(scene));
  }

  public static  getLastCharacterPlayed(scene: Phaser.Scene): string
  {
    return scene.game.registry.get(GameDataHandler.keys.SelectedCharacter) ?? "";
  }

  public static updateLastLevelPlayed(scene: Phaser.Scene, level: LevelNameValues) {
    scene.game.registry.set(GameDataHandler.keys.LastLevelPlayed, level);
  }

  public static updateLastWorldPlayed(scene: Phaser.Scene, world: number) {
    scene.game.registry.set(GameDataHandler.keys.LastWorldPlayed, world);
  }

  public static updateSelectedCharacter(scene:Phaser.Scene,character:string)
  {
    scene.game.registry.set(GameDataHandler.keys.SelectedCharacter,character);
  }

  public static isLevelUnlocked(scene: Phaser.Scene, levelId: LevelNameValues): boolean {
    let unlockedLevels: LevelNameValues[] = scene.game.registry.get(GameDataHandler.keys.UnlockedLevels);
    return unlockedLevels.indexOf(levelId) !== -1;
  }

  public static getLastLevelUnlocked(scene:Phaser.Scene)
  {
    let unlockedLevels: LevelNameValues[] = scene.game.registry.get(GameDataHandler.keys.UnlockedLevels);
    return unlockedLevels[unlockedLevels.length-1];
  }

  public static getWorldForLevel(scene:Phaser.Scene, level:LevelNameValues)
  {
    for(let i = 0; i < Worlds.length; ++i)
    {
      let levels = Worlds[i].levels as string[];
      if(levels.indexOf(level) > -1)
      {
        console.log("getting world", Worlds[i]);
        return i;
      }
    }

    return 0;
  }

  public static unlockLevel(scene: Phaser.Scene, unlockedLevel: LevelNameValues) {
    let unlockedLevels: LevelNameValues[] = scene.game.registry.get(GameDataHandler.keys.UnlockedLevels);
    let justUnlockedLevels: LevelNameValues[] = scene.game.registry.get(GameDataHandler.keys.JustUnlockedLevels);

    if (unlockedLevels.indexOf(unlockedLevel) == -1) {
      unlockedLevels.push(unlockedLevel);
      scene.game.registry.set(GameDataHandler.keys.UnlockedLevels, unlockedLevels);

      if (justUnlockedLevels.indexOf(unlockedLevel) == -1) {
        justUnlockedLevels.push(unlockedLevel);
        scene.game.registry.set(GameDataHandler.keys.JustUnlockedLevels, justUnlockedLevels);
      }
    }
  }

  public static unlockAllLevels(scene: Phaser.Scene)
  {
    for (let level in LevelName) {
      this.unlockLevel(scene,level as LevelNameValues);
    }
  }

  public static clearJustUnlockedLevels(scene: Phaser.Scene) {
    scene.game.registry.set(GameDataHandler.keys.JustUnlockedLevels, []);
  }

  //#endregion

  public applySavedData(savedData: any): void {
    this.gameData = Object.assign({}, this.permanentDefaultData, savedData);
  }

  public applyInstanceDefaultsToPhaserRegistry(game: Phaser.Game) {
    game.registry.set(this.instanceDefaultData);
  }

  public applySavedDataToPhaserRegistry(game: Phaser.Game) {
    game.registry.set(this.gameData);
  }

  public save() {
    this.app.LocalStorageHandler.save(this.gameData);
  }

  public bindToPhaserRegistry(game: Phaser.Game) {
    game.registry.events.on(GameDataHandler.events.ChangeHighScore, this.onChangeHighScore, this);
    game.registry.events.on(GameDataHandler.events.ChangeLevelHighScores, this.onChangeLevelHighScore, this);
    game.registry.events.on(GameDataHandler.events.ChangeLevelStarScores, this.onChangeLevelStarScore, this);
    game.registry.events.on(GameDataHandler.events.ChangeEarnedPoints, this.onChangeEarnedPoints, this);
    game.registry.events.on(GameDataHandler.events.ChangeUnlockedLevels, this.onLevelUnlocked, this);
    game.registry.events.on(GameDataHandler.events.ChangeJustUnlockedLevels, this.onLevelJustUnlocked, this);
  }

  //#region Phaser Registry Change Methods

  protected onChangeHighScore(parent: any, value: any, previousValue: any) {
    this.gameData[GameDataHandler.keys.HighScore] = value;
    this.save();
  }

  protected onChangeLevelHighScore(parent: any, value: any, previousValue: any) {
    this.gameData[GameDataHandler.keys.LevelHighScores] = value;
    this.save();
  }

  protected onChangeLevelStarScore(parent: any, value: any, previousValue: any) {
    this.gameData[GameDataHandler.keys.LevelStarScores] = value;
    this.save();
  }

  protected onChangeEarnedPoints(parent: any, value: any, previousValue: any) {
    this.gameData[GameDataHandler.keys.EarnedPoints] = value;
    this.save();
  }

  protected onLevelUnlocked(parent: any, value: any, previousValue: any) {
    this.gameData[GameDataHandler.keys.UnlockedLevels] = value;
    this.save();
  }

  protected onLevelJustUnlocked(parent: any, value: any, previousValue: any) {
    this.gameData[GameDataHandler.keys.JustUnlockedLevels] = value;
    this.save();
  }

  //#endregion
}
